<script setup lang="ts">
import { Form, Link, usePage } from '@inertiajs/vue3';
import { ChevronDown, LockKeyhole, LogOut } from '@lucide/vue';
import { computed, ref } from 'vue';
import OrgNavigation from '@/components/org/OrgNavigation.vue';
import { buttonVariants } from '@/components/ui/button';
import { Sidebar, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { localForm } from '@/lib/localHref';
import { orgNavigation } from '@/lib/orgNavigation';
import { cn } from '@/lib/utils';
import { logout } from '@/routes/org';

interface OrgStaff {
    id: string;
    name: string;
    email: string;
}
const page = usePage();
const staff = computed<OrgStaff | null>(() => {
    const identity = (
        page.props.orgAuth as { staff?: OrgStaff | null } | undefined
    )?.staff;
    return identity &&
        typeof identity.name === 'string' &&
        typeof identity.email === 'string'
        ? identity
        : null;
});
const capabilities = computed<string[]>(
    () => (page.props.capabilities as string[] | undefined) ?? [],
);
const navigation = computed(() => orgNavigation(capabilities.value));
const activeItem = computed(() => {
    const path = page.url.split('?')[0];
    return [...navigation.value]
        .sort((a, b) => b.href.length - a.href.length)
        .find(
            (item) => path === item.href || path?.startsWith(item.href + '/'),
        );
});
const initials = computed(
    () =>
        staff.value?.name
            ?.trim()
            .split(/\s+/)
            .slice(0, 2)
            .map((part) => part[0])
            .join('')
            .toUpperCase() || 'PE',
);
const accountOpen = ref(false);
</script>

<template>
    <SidebarProvider
        v-slot="{ setOpenMobile }"
        :default-open="true"
        class="org min-h-dvh bg-background text-foreground antialiased"
    >
        <a
            href="#org-main"
            class="sr-only focus:not-sr-only focus:fixed focus:start-4 focus:top-4 focus:z-50 focus:rounded-xl focus:bg-primary focus:px-4 focus:py-3 focus:text-sm focus:text-primary-foreground"
            >Skip to content</a
        >

        <Sidebar
            side="left"
            collapsible="offcanvas"
            class="org"
            aria-label="Organization sidebar"
        >
            <Link
                :href="navigation[0]?.href"
                class="flex h-20 shrink-0 items-center gap-3 px-7"
                aria-label="ProExpert staff workspace"
            >
                <img
                    src="/assets/app/logos/logo-dark.3afd2a7545dd.png"
                    alt="ProExpert"
                    class="h-7 w-auto dark:hidden"
                />
                <img
                    src="/assets/app/logos/logo-light.0fb850e2b68d.png"
                    alt="ProExpert"
                    class="hidden h-7 w-auto dark:block"
                />
            </Link>
            <div
                class="mx-4 mb-6 rounded-xl border border-border bg-muted/50 px-3 py-3"
            >
                <p class="text-sm font-semibold">Organization</p>
                <p class="mt-1 text-xs text-muted-foreground">
                    People, content and operations
                </p>
            </div>
            <div class="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
                <OrgNavigation
                    :items="navigation"
                    :active-key="activeItem?.key ?? null"
                    @navigate="setOpenMobile(false)"
                />
            </div>
            <div
                class="flex shrink-0 items-center gap-2 border-t border-border px-6 py-5 text-xs text-muted-foreground"
            >
                <LockKeyhole class="size-3.5" aria-hidden="true" />
                Private staff workspace
            </div>
        </Sidebar>

        <div class="min-w-0 flex-1">
            <header class="sticky top-0 z-20 border-b border-border bg-card">
                <div
                    class="flex min-h-18 items-center justify-between gap-3 px-4 sm:px-8"
                >
                    <div class="flex min-w-0 items-center gap-3">
                        <SidebarTrigger
                            class="size-11 shrink-0"
                            @click="accountOpen = false"
                        />
                        <div class="flex min-w-0 items-center gap-3 text-sm">
                            <span class="hidden text-muted-foreground sm:inline"
                                >Organization</span
                            >
                            <span
                                class="hidden text-muted-foreground/50 sm:inline"
                                aria-hidden="true"
                                >/</span
                            >
                            <span class="truncate font-medium">{{
                                activeItem?.label ?? 'Workspace'
                            }}</span>
                        </div>
                    </div>

                    <DropdownMenu
                        v-if="staff"
                        v-model:open="accountOpen"
                        @update:open="setOpenMobile(false)"
                    >
                        <DropdownMenuTrigger
                            :class="
                                cn(
                                    buttonVariants({ variant: 'ghost' }),
                                    'h-11 gap-3 rounded-xl px-2',
                                )
                            "
                            aria-label="Open staff account menu"
                        >
                            <span
                                class="flex size-8 shrink-0 items-center justify-center rounded-lg border border-primary/15 bg-primary/10 text-xs font-semibold text-primary"
                                >{{ initials }}</span
                            >
                            <span
                                class="hidden max-w-40 truncate text-sm sm:block"
                                >{{ staff.name }}</span
                            >
                            <ChevronDown
                                class="text-muted-foreground"
                                aria-hidden="true"
                            />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            :side-offset="10"
                            class="w-64 rounded-xl border-border p-1.5 shadow-lg"
                        >
                            <DropdownMenuLabel
                                class="mb-1 rounded-lg bg-muted/60 px-3 py-3 font-normal"
                            >
                                <p class="truncate text-sm font-semibold">
                                    {{ staff.name }}
                                </p>
                                <p
                                    class="mt-1 truncate text-xs text-muted-foreground"
                                >
                                    {{ staff.email }}
                                </p>
                            </DropdownMenuLabel>
                            <DropdownMenuGroup>
                                <Form
                                    v-bind="localForm(logout.form())"
                                    #default="{ processing, submit }"
                                >
                                    <DropdownMenuItem
                                        :disabled="processing"
                                        class="min-h-11 gap-3 rounded-lg px-3"
                                        @select.prevent="submit()"
                                    >
                                        <LogOut aria-hidden="true" />
                                        {{
                                            processing
                                                ? 'Signing out...'
                                                : 'Sign out'
                                        }}
                                    </DropdownMenuItem>
                                </Form>
                            </DropdownMenuGroup>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            <main id="org-main" tabindex="-1" class="min-w-0 px-4 pb-8 sm:px-8">
                <slot />
            </main>
        </div>
    </SidebarProvider>
</template>
